import React from 'react';
import { User, Briefcase, GraduationCap, Check, Trash2, Plus, X, Wand2, ChevronLeft, ChevronRight, Star, Heart, Award, Globe, Zap, Book, Code, Coffee, Music, Camera, Gamepad2, Smile, Languages } from 'lucide-react';
import SectionHeader from './ui/SectionHeader';
import InputGroup from './ui/InputGroup';
import SuggestionDraggable from './ui/SuggestionDraggable';
import { SUGGESTIONS } from '../constants.js';
import { generateAIContent, getPrompts } from '../aiService';
import { Loader2, ArrowUp, ArrowDown, PlusCircle, Sparkles } from 'lucide-react';
import CustomSectionEditor from './ui/CustomSectionEditor';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const ICON_MAP = {
    'Star': Star,
    'Heart': Heart,
    'Award': Award,
    'Globe': Globe,
    'Zap': Zap,
    'Book': Book,
    'Code': Code,
    'Coffee': Coffee,
    'Music': Music,
    'Camera': Camera,
    'Gamepad': Gamepad2,
    'Smile': Smile,
    'Briefcase': Briefcase,
    'GraduationCap': GraduationCap,
    'Check': Check,
    'User': User
};

const EditorPanel = ({
    resumeData,
    setResumeData,
    activeTab,
    setActiveTab,
    handleDropText,
    addExperience,
    removeExperience,
    updateExperience,
    addEducation,
    removeEducation,
    updateEducation,
    addSkill,
    removeSkill,
    addCustomSection,
    removeCustomSection,
    updateCustomSection,
    reorderSections,
    handleDragStart,
    apiConfig,
    openAIModal,
    showNotification
}) => {
    const { isDarkMode } = useTheme();
    const { purchaseFeature, hasFeature, getFeaturePrice } = useSubscription();
    const [loadingAI, setLoadingAI] = React.useState(null);
    const [sidebarOpen, setSidebarOpen] = React.useState(window.innerWidth > 768);
    const [newSectionTitle, setNewSectionTitle] = React.useState("");
    const [selectedIcon, setSelectedIcon] = React.useState("Star");
    const [selectedLanguage, setSelectedLanguage] = React.useState('English');

    const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Dutch', 'Chinese', 'Japanese', 'Hindi', 'Arabic', 'Finnish', 'Vietnamese'];

    // -- Custom Suggestions State --
    const [suggestions, setSuggestions] = React.useState(() => {
        const saved = localStorage.getItem('custom_suggestions');
        return saved ? JSON.parse(saved) : SUGGESTIONS;
    });
    const [addingToCategory, setAddingToCategory] = React.useState(null); // 'tech', 'general', etc.
    const [newSuggestionText, setNewSuggestionText] = React.useState("");

    const handleAddSuggestion = (category) => {
        if (!newSuggestionText.trim()) return;

        const newSuggestions = {
            ...suggestions,
            [category]: [...(suggestions[category] || []), newSuggestionText.trim()]
        };

        setSuggestions(newSuggestions);
        localStorage.setItem('custom_suggestions', JSON.stringify(newSuggestions));
        setNewSuggestionText("");
        setAddingToCategory(null);
    };

    const handleMoveSection = (index, direction) => {
        const newOrder = [...resumeData.sectionOrder];
        if (direction === 'up' && index > 0) {
            [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
        } else if (direction === 'down' && index < newOrder.length - 1) {
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        }
        reorderSections(newOrder);
    };

    const handleAddSection = () => {
        if (newSectionTitle.trim()) {
            addCustomSection(newSectionTitle, selectedIcon);
            setNewSectionTitle("");
            setSelectedIcon("Star");
        }
    };

    const hasApiKey = apiConfig && (apiConfig.gemini || apiConfig.openai || apiConfig.anthropic);

    const handleGenerateAI = async (type, id = null) => {
        if (!hasApiKey) {
            openAIModal();
            return;
        }

        setLoadingAI(id || type);

        try {
            let prompt = "";
            if (type === 'summary') {
                prompt = getPrompts('summary', {
                    role: resumeData.experience[0]?.role || "Professional",
                    skills: resumeData.skills.join(", ")
                }, selectedLanguage);
            } else if (type === 'experience') {
                const exp = resumeData.experience.find(e => e.id === id);
                prompt = getPrompts('experience', exp, selectedLanguage);
            }

            const content = await generateAIContent(apiConfig, prompt);

            if (type === 'summary') {
                setResumeData(prev => ({ ...prev, personal: { ...prev.personal, summary: content } }));
            } else if (type === 'experience') {
                updateExperience(id, 'description', content);
            }
        } catch (error) {
            alert("AI Generation Failed: " + error.message);
        } finally {
            setLoadingAI(null);
        }
    };

    const handleSkillDrop = (e) => {
        e.preventDefault();
        const skill = e.dataTransfer.getData("text/plain");
        addSkill(skill);
    };

    const handleEnhanceSkill = async (skill) => {
        // 1. If no API key, we can't do the magic
        if (!hasApiKey) {
            openAIModal();
            return;
        }

        // 2. Trigger AI Magic
        setLoadingAI(skill); // Use skill name as loading ID
        try {
            const prompt = getPrompts('skill-enhancement', { skill }, selectedLanguage);
            const responseText = await generateAIContent(apiConfig, prompt);

            // Parse JSON response
            const jsonStr = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
            const data = JSON.parse(jsonStr);

            // 3. Update Resume Data
            setResumeData(prev => {
                const newData = { ...prev };

                // Append to summary
                if (data.summarySentence) {
                    newData.personal.summary = (newData.personal.summary ? newData.personal.summary + " " : "") + data.summarySentence;
                }

                // Append to most recent experience
                if (data.experienceBullet && newData.experience.length > 0) {
                    const firstExp = newData.experience[0];
                    firstExp.description = (firstExp.description ? firstExp.description + "\n• " : "• ") + data.experienceBullet;
                }

                return newData;
            });

            if (showNotification) {
                showNotification("AI Content Generated for " + skill + "!");
            }

        } catch (error) {
            console.error("Skill Magic Error:", error);
            if (showNotification) {
                showNotification("AI Generation Failed");
            }
        } finally {
            setLoadingAI(null);
        }

    };

    const handleTranslateResume = async () => {
        if (!hasApiKey) {
            openAIModal();
            return;
        }

        // Logic for translation confirmation state
        if (loadingAI !== 'confirm-translate') {
            setLoadingAI('confirm-translate');
            return;
        }

        // If 'confirm-translate' matches, we proceed below.
        setLoadingAI('translating');

        try {
            const prompt = getPrompts('translate-resume', resumeData, selectedLanguage);
            const responseText = await generateAIContent(apiConfig, prompt);

            // Parse JSON response
            const jsonStr = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
            const translatedData = JSON.parse(jsonStr);

            // Merge safely
            setResumeData(prev => ({
                ...prev,
                ...translatedData,
                sectionOrder: prev.sectionOrder // preserve order just in case
            }));

            if (showNotification) {
                showNotification(`Resume translated to ${selectedLanguage}!`);
            }
        } catch (error) {
            console.error("Translation Error:", error);
            alert("Translation failed: " + error.message);
        } finally {
            setLoadingAI(null);
        }
    };

    const handleSmartImprove = async () => {
        // Differential Pricing: Check if unlocked
        if (!hasFeature('polish_resume')) {
            purchaseFeature('polish_resume');
            return;
        }

        console.log("Improvement clicked. Config:", apiConfig);
        if (!hasApiKey) {
            openAIModal();
            return;
        }

        // Check if we already waiting for confirmation (handling in button state instead)
        // If we want a separate modal, we'd add state. For now, let's trust the button state if implemented, 
        // OR better: use the browser confirm but ensure it's not being auto-dimissed. 
        // Actually, user says "flash and disappear", which implies the *modal* (like AI Settings) or the browser alert? 
        // If it's the AI Settings modal flashing, then `hasApiKey` is toggling. 
        // If it's `window.confirm` flashing, that's weird. 
        // Let's rely on a custom UI confirmation state instead of window.confirm to be safe.

        // REPLACING window.confirm LOGIC WITH STATE-BASED CONFIRMATION
        if (loadingAI === 'confirm-polish') {
            setLoadingAI('polishing');
        } else {
            setLoadingAI('confirm-polish');
            return;
        }

        console.log("Starting smart improvement...");
        setLoadingAI('polishing');

        try {
            // 1. Polish Summary
            console.log("Polishing Summary...");
            setLoadingAI('polishing-summary'); // Optional: can be used for UI feedback

            try {
                const summaryPrompt = getPrompts('improve-summary', {
                    currentSummary: resumeData.personal.summary,
                    role: resumeData.experience[0]?.role || "Professional",
                    skills: resumeData.skills.join(", ")
                }, selectedLanguage);

                const summaryRes = await generateAIContent(apiConfig, summaryPrompt);
                const jsonStr = summaryRes.replace(/```(?:json)?/gi, '').trim();
                const summaryData = JSON.parse(jsonStr);

                if (summaryData.summary) {
                    setResumeData(prev => ({
                        ...prev,
                        personal: { ...prev.personal, summary: summaryData.summary }
                    }));
                }
            } catch (err) {
                console.error("Summary polish failed, continuing...", err);
            }

            // 2. Polish Experience Items (Sequentially)
            console.log("Polishing Experience...");
            const experiences = [...resumeData.experience];
            let improvedCount = 0;

            for (let i = 0; i < experiences.length; i++) {
                const exp = experiences[i];
                setLoadingAI(`polishing-exp-${i}`); // Feedback opportunity

                try {
                    const expPrompt = getPrompts('improve-experience-entry', {
                        role: exp.role,
                        company: exp.company,
                        description: exp.description
                    }, selectedLanguage);

                    const expRes = await generateAIContent(apiConfig, expPrompt);
                    const expData = JSON.parse(expRes.replace(/```json/g, '').replace(/```/g, '').trim());

                    if (expData.description) {
                        setResumeData(prev => ({
                            ...prev,
                            experience: prev.experience.map(e => e.id === exp.id ? { ...e, description: expData.description } : e)
                        }));
                        improvedCount++;
                    }

                    // Delay to respect rate limits (Gemini Free Tier is often strict)
                    await new Promise(r => setTimeout(r, 2000));

                } catch (err) {
                    console.error(`Experience polish failed for ${exp.company}`, err);
                }
            }

            if (showNotification) showNotification(`✨ Resume polished! (Summary + ${improvedCount} jobs updated)`);

        } catch (error) {
            console.error("Improvement Error:", error);
            if (showNotification) showNotification("Error polishing resume: " + error.message);
        } finally {
            console.log("Improvement finished.");
            setLoadingAI(null);
        }
    };

    return (
        <div className="flex h-full relative">
            {/* Main Editor Column */}
            <div className={`flex-1 flex flex-col overflow-hidden ${isDarkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
                <div className="w-full max-w-3xl mx-auto px-6 pt-6 md:px-12 md:pt-12">
                    <header className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className={`text-2xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                Resume Editor
                            </h1>
                            <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}> customize your resume content</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div id="nav-translator" className={`flex items-center gap-1 rounded-md px-2 py-1 ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-100'}`}>
                                <Globe size={14} className={isDarkMode ? "text-slate-400" : "text-slate-500"} />
                                <select
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                    className={`bg-transparent text-xs font-medium outline-none cursor-pointer border-none p-0 focus:ring-0 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}
                                >
                                    {LANGUAGES.map(lang => (
                                        <option key={lang} value={lang}>{lang}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={handleTranslateResume}
                                disabled={loadingAI === 'translating'}
                                className={`px-2 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1
                                    ${loadingAI === 'confirm-translate'
                                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                        : isDarkMode
                                            ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                    }`}
                                title="Translate Entire Resume"
                            >
                                <Languages size={14} />
                                {loadingAI === 'translating' ? 'Translating...' : (loadingAI === 'confirm-translate' ? 'Confirm?' : 'Translate All')}
                            </button>
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                id="btn-smart-assistant"
                                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors
                                    ${isDarkMode
                                        ? 'bg-purple-900/30 text-purple-400 hover:bg-purple-900/50'
                                        : 'bg-purple-50 text-purple-600 hover:bg-purple-100'}`}
                            >
                                <Wand2 size={12} />
                                {sidebarOpen ? 'Hide' : 'Show'} Assistant
                                {sidebarOpen ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
                            </button>
                        </div>
                    </header>
                </div>

                {/* Editor Content Area */}
                <div className="flex-1 overflow-y-auto px-6 pb-20 md:px-12 scroll-smooth">
                    <div className="max-w-3xl mx-auto space-y-8">
                        {/* Personal Info */}
                        <section>
                            <SectionHeader icon={User} title="Personal Details" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputGroup label="Full Name" value={resumeData.personal.fullName} onChange={(e) => setResumeData({ ...resumeData, personal: { ...resumeData.personal, fullName: e.target.value } })} placeholder="e.g. John Doe" />
                                <InputGroup label="Email" value={resumeData.personal.email} onChange={(e) => setResumeData({ ...resumeData, personal: { ...resumeData.personal, email: e.target.value } })} placeholder="john@example.com" />
                                <InputGroup label="Phone" value={resumeData.personal.phone} onChange={(e) => setResumeData({ ...resumeData, personal: { ...resumeData.personal, phone: e.target.value } })} placeholder="(555) 555-5555" />
                                <InputGroup label="Location" value={resumeData.personal.location} onChange={(e) => setResumeData({ ...resumeData, personal: { ...resumeData.personal, location: e.target.value } })} placeholder="New York, NY" />
                            </div>
                            <div className="mt-4">
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-slate-700">Professional Summary</label>
                                    <button
                                        id="btn-generate-summary"
                                        onClick={() => handleGenerateAI('summary')}
                                        disabled={loadingAI === 'summary'}
                                        className={`text-xs flex items-center gap-1 font-medium disabled:opacity-50
                                        ${isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                                    >
                                        {loadingAI === 'summary' ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                        Generate with AI
                                    </button>
                                </div>
                                <InputGroup
                                    multiline
                                    value={resumeData.personal.summary}
                                    onChange={(e) => setResumeData({ ...resumeData, personal: { ...resumeData.personal, summary: e.target.value } })}
                                    onDrop={(e) => handleDropText(e, 'summary', 'personal')}
                                    placeholder="Drag & drop suggestions here or type your summary..."
                                />
                            </div>
                        </section>

                        {/* Dynamic Sections */}
                        {resumeData.sectionOrder.map((sectionId, index) => {
                            const isFirst = index === 0;
                            const isLast = index === resumeData.sectionOrder.length - 1;

                            return (
                                <div key={sectionId} className="relative group/section-container">
                                    {/* Reordering Controls */}
                                    <div className="absolute -left-8 top-4 flex flex-col gap-1 opacity-0 group-hover/section-container:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleMoveSection(index, 'up')}
                                            disabled={isFirst}
                                            className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-blue-500 disabled:opacity-30"
                                        >
                                            <ArrowUp size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleMoveSection(index, 'down')}
                                            disabled={isLast}
                                            className="p-1 bg-white border border-slate-200 rounded text-slate-400 hover:text-blue-500 disabled:opacity-30"
                                        >
                                            <ArrowDown size={14} />
                                        </button>
                                    </div>

                                    {/* Render Section Content */}
                                    {sectionId === 'experience' && (
                                        <section>
                                            <SectionHeader icon={Briefcase} title="Experience" onAdd={addExperience} />
                                            <div className="space-y-6">
                                                {resumeData.experience.map((exp, idx) => (
                                                    <div key={exp.id} className={`p-4 rounded-xl border relative group transition-all hover:shadow-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                        <button onClick={() => removeExperience(exp.id)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Trash2 size={16} />
                                                        </button>
                                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                                            <InputGroup label="Role" value={exp.role} onChange={(e) => updateExperience(exp.id, 'role', e.target.value)} placeholder="Software Engineer" />
                                                            <InputGroup label="Company" value={exp.company} onChange={(e) => updateExperience(exp.id, 'company', e.target.value)} placeholder="Tech Co." />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3 mb-3">
                                                            <InputGroup label="Start Date" value={exp.startDate} onChange={(e) => updateExperience(exp.id, 'startDate', e.target.value)} placeholder="MM/YYYY" />
                                                            <InputGroup label="End Date" value={exp.endDate} onChange={(e) => updateExperience(exp.id, 'endDate', e.target.value)} placeholder="Present" />
                                                        </div>
                                                        <div className="flex justify-between items-center mb-1 mt-3">
                                                            <label className="block text-sm font-medium text-slate-700">Description</label>
                                                            <button
                                                                onClick={() => handleGenerateAI('experience', exp.id)}
                                                                disabled={loadingAI === exp.id}
                                                                className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                                                            >
                                                                {loadingAI === exp.id ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                                                Generate with AI
                                                            </button>
                                                        </div>
                                                        <InputGroup
                                                            multiline
                                                            value={exp.description}
                                                            onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                                                            onDrop={(e) => handleDropText(e, 'description', 'experience', idx)}
                                                            placeholder="Drag Action Verbs or Tech skills here..."
                                                        />
                                                    </div>
                                                ))}
                                                {resumeData.experience.length === 0 && (
                                                    <div onClick={addExperience} className="text-center p-8 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all">
                                                        <Plus size={24} className="mx-auto mb-2" />
                                                        <p>Add Work Experience</p>
                                                    </div>
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    {sectionId === 'education' && (
                                        <section>
                                            <SectionHeader icon={GraduationCap} title="Education" onAdd={addEducation} />
                                            <div className="space-y-4">
                                                {resumeData.education.map((edu) => (
                                                    <div key={edu.id} className={`p-4 rounded-xl border relative group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                                                        <button onClick={() => removeEducation(edu.id)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Trash2 size={16} />
                                                        </button>
                                                        <InputGroup label="School" value={edu.school} onChange={(e) => updateEducation(edu.id, 'school', e.target.value)} placeholder="University Name" />
                                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                                            <InputGroup label="Degree" value={edu.degree} onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)} placeholder="Bachelor's" />
                                                            <InputGroup label="Graduation Year" value={edu.year} onChange={(e) => updateEducation(edu.id, 'year', e.target.value)} placeholder="2024" />
                                                        </div>
                                                    </div>
                                                ))}
                                                {resumeData.education.length === 0 && (
                                                    <div onClick={addEducation} className="text-center p-6 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 cursor-pointer hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all">
                                                        <Plus size={20} className="mx-auto mb-1" />
                                                        <p className="text-sm">Add Education</p>
                                                    </div>
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    {sectionId === 'skills' && (
                                        <section>
                                            <SectionHeader icon={Check} title="Skills" />
                                            <div className={`p-4 rounded-xl border min-h-[100px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                                                onDrop={handleSkillDrop}
                                                onDragOver={(e) => e.preventDefault()}
                                            >
                                                <div className="flex flex-wrap gap-2">
                                                    {resumeData.skills.map((skill, idx) => (
                                                        <span key={idx} className="bg-white border border-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm flex items-center gap-2 shadow-sm group">
                                                            {skill}
                                                            <div className="flex items-center border-l border-slate-200 pl-2 ml-1 gap-1">
                                                                <button
                                                                    id={idx === 0 ? "btn-skill-wand-0" : undefined}
                                                                    onClick={() => handleEnhanceSkill(skill)}
                                                                    disabled={loadingAI === skill}
                                                                    className="text-purple-400 hover:text-purple-600 disabled:opacity-50"
                                                                    title="Generate content for this skill"
                                                                >
                                                                    {loadingAI === skill ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                                                                </button>
                                                                <button onClick={() => removeSkill(skill)} className="text-slate-300 hover:text-red-500">
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        </span>
                                                    ))}
                                                    {resumeData.skills.length === 0 && (
                                                        <p className="text-slate-400 text-sm italic w-full text-center py-4">Drag skills from the Assistant tab here...</p>
                                                    )}
                                                </div>
                                            </div>
                                        </section>
                                    )}

                                    {/* Custom Sections */}
                                    {typeof sectionId === 'number' && (
                                        <section>
                                            <SectionHeader
                                                icon={ICON_MAP[resumeData.customSections.find(s => s.id === sectionId)?.icon] || Star}
                                                title={resumeData.customSections.find(s => s.id === sectionId)?.title || "Custom Section"}
                                            />
                                            <CustomSectionEditor
                                                section={resumeData.customSections.find(s => s.id === sectionId)}
                                                onUpdate={updateCustomSection}
                                                onDelete={removeCustomSection}
                                            />
                                        </section>
                                    )}
                                </div>
                            );
                        })}

                        {/* Add Custom Section */}
                        <div className="pt-6 border-t border-slate-200">
                            <h3 className="text-sm font-bold text-slate-700 mb-3">Add Custom Section</h3>
                            <div className="flex flex-col gap-3">
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                    {Object.keys(ICON_MAP).filter(k => !['Briefcase', 'GraduationCap', 'Check', 'User'].includes(k)).map(iconName => {
                                        const Icon = ICON_MAP[iconName];
                                        return (
                                            <button
                                                key={iconName}
                                                onClick={() => setSelectedIcon(iconName)}
                                                className={`p-2 rounded-lg border transition-all ${selectedIcon === iconName
                                                    ? 'bg-blue-50 border-blue-500 text-blue-600'
                                                    : isDarkMode
                                                        ? 'bg-slate-800 border-slate-700 text-slate-500 hover:border-blue-500 hover:text-blue-400'
                                                        : 'bg-white border-slate-200 text-slate-400 hover:border-blue-300'
                                                    }`}
                                                title={iconName}
                                            >
                                                <Icon size={18} />
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newSectionTitle}
                                        onChange={(e) => setNewSectionTitle(e.target.value)}
                                        placeholder="e.g. Hobbies, Certifications"
                                        className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none
                                        ${isDarkMode
                                                ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
                                                : 'bg-white border-slate-200'}`}
                                    />
                                    <button
                                        onClick={handleAddSection}
                                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <PlusCircle size={16} />
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Collapsible Sidebar */}
            <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden border-l flex flex-col ${isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                {sidebarOpen && (
                    <>
                        <div className={`p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wider flex items-center gap-2">
                                <Wand2 size={14} />
                                Smart Assistant
                            </h3>
                            <button
                                onClick={handleSmartImprove}
                                disabled={loadingAI && loadingAI !== 'confirm-polish'}
                                className={`w-full mt-3 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition-all shadow-sm ${loadingAI === 'confirm-polish'
                                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                    : isDarkMode
                                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white'
                                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white'
                                    } ${(loadingAI && loadingAI !== 'confirm-polish') ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {
                                    (loadingAI && typeof loadingAI === 'string' && loadingAI.startsWith('polishing'))
                                        ? <Loader2 size={12} className="animate-spin" />
                                        : (loadingAI === 'confirm-polish' ? <Check size={12} /> : (hasFeature('polish_resume') ? <Sparkles size={12} /> : <div className="text-[10px] bg-white/20 px-1 rounded">${getFeaturePrice('polish_resume')}</div>))
                                }
                                {
                                    (loadingAI && typeof loadingAI === 'string' && loadingAI.startsWith('polishing'))
                                        ? (loadingAI === 'polishing-summary' ? 'Improving Summary...' : 'Improving Experience...')
                                        : (loadingAI === 'confirm-polish' ? 'Are you sure? Click to confirm' : (hasFeature('polish_resume') ? 'Polish Entire Resume' : `Unlock Polish ($${getFeaturePrice('polish_resume')})`))
                                }
                            </button>

                            <p className="text-[10px] text-slate-500 mt-2 text-center">
                                Uses AI to rewrite summary & bullets professionally.
                            </p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {Object.entries(suggestions).map(([category, items]) => (
                                <div key={category}>
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">{category}</h4>
                                        <button
                                            onClick={() => {
                                                setAddingToCategory(addingToCategory === category ? null : category);
                                                setNewSuggestionText("");
                                            }}
                                            className="text-slate-400 hover:text-blue-500 p-1 hover:bg-slate-200 rounded"
                                            title={`Add to ${category}`}
                                        >
                                            <Plus size={12} />
                                        </button>
                                    </div>

                                    {addingToCategory === category && (
                                        <div className="mb-2 flex gap-1 items-center animate-in fade-in slide-in-from-top-1">
                                            <input
                                                type="text"
                                                autoFocus
                                                value={newSuggestionText}
                                                onChange={(e) => setNewSuggestionText(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddSuggestion(category)}
                                                className="flex-1 text-xs border border-blue-400 rounded px-2 py-1 outline-none shadow-sm"
                                                placeholder="Type & Enter..."
                                            />
                                            <button
                                                onClick={() => handleAddSuggestion(category)}
                                                className="text-white bg-blue-500 hover:bg-blue-600 rounded p-1"
                                            >
                                                <Check size={12} />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-2">
                                        {items.map((item, i) => (
                                            <div key={i} className="flex group">
                                                <SuggestionDraggable text={item} onDragStart={handleDragStart} />
                                                <button
                                                    onClick={() => addSkill(item)}
                                                    className="bg-white border-y border-r border-slate-200 hover:bg-blue-50 text-slate-400 hover:text-blue-500 px-2 rounded-r-lg mb-2 ml-[-4px] z-10 hidden group-hover:flex items-center"
                                                    title="Quick Add to Skills"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};

export default EditorPanel;
